import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/tw_common';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-bold ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 tracking-wide',
  {
    variants: {
      variant: {
        default: 'bg-white text-b_grey_2 border-b_grey_1 border-0 border-b-4 active:border-b-0 hover:bg-b_grey_3',
        primary: 'bg-b_blue_3 text-white border-b_blue_4 border-0 border-b-4 active:border-b-0',
        error: 'bg-b_red_1 text-white border-b_red_2 border-0 border-b-4 active:border-b-0',
        success: 'bg-b_green_3 text-white border-b_green_4 border-0 border-b-4 active:border-b-0',

        bordered_default: 'bg-white text-b_grey_2 border-b_grey_1 border-2 border-b-4 active:border-b-2 hover:bg-b_grey_3',
        bordered_primary: 'bg-b_blue_1 text-b_blue_6 border-b_blue_2 border-2 border-b-4 active:border-b-2',
        bordered_error: 'bg-b_red_3 text-b_red_2 border-b_red_2 border-2 border-b-4 active:border-b-2',
        bordered_success: 'bg-b_green_1 text-b_green_2 border-b_green_2 border-2 border-b-4 active:border-b-2',
        bordered_invisible: 'bg-b_grey_1 text-b_grey_1 border-b_grey_1 border-2 border-b-4 active:border-b-2 hover:bg-b_grey_1 select-none',
        // bordered_invisible: 'bg-b_b_grey_1 text-b_grey_1 border-b_grey_1 boder-2 border-b-4 active:border-b-2',
        bordered_light_default: 'bg-white text-b_grey_2 border-b_grey_1 border border-b-5 active:border-b-2 hover:bg-b_grey_3',

        btn_correct: 'bg-b_correct1 text-white border-b_correct2 border-0 border-b-4 active:border-b-0',
        btn_wrong: 'bg-b_incorrect1 text-white border-b_incorrect2 border-0 border-b-4 active:border-b-0',

        confirm: 'bg-b_blue_8 text-white border-b_blue_9 border-0 border-b-4 active:border-b-0',
        danger: 'bg-white text-b_incorrect1',
        warning: 'bg-[#E60505] text-white border-[#980A0A] border-0 border-b-4 active:border-b-0',

        ghost: 'hover:bg-accent hover:text-accent-foreground',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',

        text_default: 'text-[16px] py-[16px] !h-[48px] leading-12 !rounded-none !focus-visible:outline-none !focus-visible:[box-shadow:none]',
        text_primary: 'text-[16px] py-[16px] !h-[48px] leading-12 !rounded-none !focus-visible:outline-none !focus-visible:[box-shadow:none] text-b_blue_3 ',
        text_danger: 'text-[16px] py-[16px] !h-[48px] leading-12 !rounded-none !focus-visible:outline-none !focus-visible:[box-shadow:none] text-b_incorrect1 ',

        no_border: 'border-0',

        course_unit: '',
        course_lesson: '',
      },
      size: {
        default: 'h-11 px-4 py-2 pt-4 pb-4',
        sm: 'h-9 px-3 py-1.5 pt-2 pb-2',
        lg: 'h-12 px-8 pb-4.5',
        icon: 'h-10 w-10 p-2',
        rounded: 'rounded-full',
        lgg: 'h-[46px] px-[3rem] pb-4.5 text-[16px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
